import { useCallback, useEffect, useId, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Check, FileText, RotateCw, Upload, X } from "lucide-react";
import { motionTokens } from "../../lib/motion-tokens";
import styles from "./file-upload.module.css";
let counter = 0;
const makeId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `file-${Date.now()}-${counter++}`;
/** Extensions (".pdf"), MIME types ("application/pdf") and wildcards ("image/*"), comma separated. */
function matchesAccept(file, accept) {
  if (!accept) return true;
  const name = file.name.toLowerCase(),
    type = (file.type || "").toLowerCase();
  return accept
    .split(",")
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean)
    .some((token) =>
      token.startsWith(".")
        ? name.endsWith(token)
        : token.endsWith("/*")
          ? type.startsWith(token.slice(0, -1))
          : type === token,
    );
}
function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}
/**
 * A dropzone with a file list. Files are validated by type and size and stay listed with an error when rejected.
 * With onUpload each valid file is uploaded with progress, retry and abort-on-remove; without it files are only listed and validated.
 */
export function FileUpload({
  accept,
  maxSize,
  multiple = true,
  disabled = false,
  label = "Upload files",
  description = "Drop files here or browse from your device.",
  value,
  onChange,
  onUpload,
  className,
}) {
  const reduced = !!useReducedMotion();
  const uid = useId();
  const descId = `${uid}-description`;
  const [inner, setInner] = useState([]);
  const items = value ?? inner;
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const [progress, setProgress] = useState({});
  const [dragging, setDragging] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const controllers = useRef(new Map());
  const inputRef = useRef(null);
  const zoneRef = useRef(null);
  const listRef = useRef(null);
  useEffect(
    () => () => controllers.current.forEach((controller) => controller.abort()),
    [],
  );
  const commit = useCallback(
    (next) => {
      itemsRef.current = next;
      if (value === undefined) setInner(next);
      onChange?.(next);
    },
    [onChange, value],
  );
  const upload = useCallback(
    async (item) => {
      if (!onUpload || item.error) return;
      controllers.current.get(item.id)?.abort();
      const controller = new AbortController();
      controllers.current.set(item.id, controller);
      const set = (state) => {
        if (!controller.signal.aborted)
          setProgress((previous) => ({ ...previous, [item.id]: state }));
      };
      set({ status: "uploading", percent: 0 });
      try {
        await onUpload(item.file, {
          onProgress: (percent) =>
            set({
              status: "uploading",
              percent: Math.max(0, Math.min(100, percent)),
            }),
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        set({ status: "done", percent: 100 });
        setAnnouncement(`${item.file.name} uploaded`);
      } catch {
        if (controller.signal.aborted) return;
        set({ status: "failed", percent: 0 });
        setAnnouncement(`${item.file.name} failed to upload`);
      }
    },
    [onUpload],
  );
  function add(fileList) {
    const files = Array.from(fileList ?? []);
    if (!files.length || disabled) return;
    const picked = multiple ? files : files.slice(0, 1);
    const created = picked.map((file) => {
      let error;
      if (!matchesAccept(file, accept))
        error = "This file type isn't supported";
      else if (maxSize && file.size > maxSize)
        error = `Larger than ${formatSize(maxSize)}`;
      return { id: makeId(), file, error };
    });
    if (!multiple)
      itemsRef.current.forEach((item) =>
        controllers.current.get(item.id)?.abort(),
      );
    const kept = multiple ? itemsRef.current : [];
    commit([...kept, ...created]);
    const rejected = created.filter((item) => item.error).length,
      added = created.length - rejected;
    setAnnouncement(
      [
        added && `${added} ${added === 1 ? "file" : "files"} added`,
        rejected && `${rejected} rejected`,
      ]
        .filter(Boolean)
        .join(", "),
    );
    created.forEach(upload);
  }
  function remove(item, index) {
    controllers.current.get(item.id)?.abort();
    controllers.current.delete(item.id);
    commit(itemsRef.current.filter((entry) => entry.id !== item.id));
    setAnnouncement(`${item.file.name} removed`);
    requestAnimationFrame(() => {
      const buttons = listRef.current?.querySelectorAll("[data-remove]");
      const target =
        buttons && buttons.length
          ? buttons[Math.min(index, buttons.length - 1)]
          : zoneRef.current;
      target?.focus();
    });
  }
  const openPicker = () => {
    if (!disabled) inputRef.current?.click();
  };
  return (
    <div
      className={[styles.root, className].filter(Boolean).join(" ")}
      data-disabled={disabled || undefined}
    >
      <div
        ref={zoneRef}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        aria-describedby={descId}
        className={styles.zone}
        data-dragging={dragging || undefined}
        onClick={openPicker}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openPicker();
          }
        }}
        onDragEnter={(event) => {
          event.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget))
            setDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          add(event.dataTransfer.files);
        }}
      >
        <span className={styles.icon} aria-hidden="true">
          <Upload size={18} strokeWidth={1.75} />
        </span>
        <span className={styles.title}>
          {dragging ? "Drop to add files" : label}
        </span>
        <span id={descId} className={styles.description}>
          {description}
        </span>
        <input
          ref={inputRef}
          className={styles.input}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          tabIndex={-1}
          aria-hidden="true"
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => {
            add(event.target.files);
            event.target.value = "";
          }}
        />
      </div>

      <ul ref={listRef} className={styles.list}>
        <AnimatePresence initial={false}>
          {items.map((item, index) => {
            const state = progress[item.id];
            const uploading = state?.status === "uploading",
              done = state?.status === "done",
              failed = state?.status === "failed";
            return (
              <motion.li
                key={item.id}
                className={styles.slot}
                initial={reduced ? { opacity: 0 } : { height: 0, opacity: 0 }}
                animate={
                  reduced ? { opacity: 1 } : { height: "auto", opacity: 1 }
                }
                exit={
                  reduced
                    ? { opacity: 0, transition: { duration: 0.1 } }
                    : {
                        height: 0,
                        opacity: 0,
                        transition: {
                          height: motionTokens.spring.smooth,
                          opacity: { duration: motionTokens.duration.instant },
                        },
                      }
                }
                transition={
                  reduced
                    ? { duration: 0.12 }
                    : {
                        height: motionTokens.spring.smooth,
                        opacity: { duration: motionTokens.duration.fast },
                      }
                }
              >
                <div
                  className={styles.row}
                  data-invalid={item.error || failed || undefined}
                >
                  <FileText
                    className={styles.fileIcon}
                    size={16}
                    strokeWidth={1.75}
                    aria-hidden="true"
                  />
                  <span className={styles.body}>
                    <span className={styles.name}>{item.file.name}</span>
                    {item.error ? (
                      <span className={styles.error} role="alert">
                        {item.error}
                      </span>
                    ) : failed ? (
                      <span className={styles.error} role="alert">
                        Upload failed
                      </span>
                    ) : (
                      <span className={styles.meta}>
                        {uploading
                          ? `${Math.round(state.percent)}%`
                          : formatSize(item.file.size)}
                      </span>
                    )}
                    {uploading && (
                      <span className={styles.track} aria-hidden="true">
                        <motion.span
                          className={styles.fill}
                          initial={false}
                          animate={{ width: `${state.percent}%` }}
                          transition={
                            reduced
                              ? { duration: 0 }
                              : motionTokens.spring.smooth
                          }
                        />
                      </span>
                    )}
                  </span>
                  {done && (
                    <Check
                      className={styles.done}
                      size={16}
                      strokeWidth={2}
                      aria-hidden="true"
                    />
                  )}
                  {failed && (
                    <button
                      type="button"
                      className={styles.action}
                      aria-label={`Retry ${item.file.name}`}
                      onClick={() => upload(item)}
                    >
                      <RotateCw
                        size={14}
                        strokeWidth={1.75}
                        aria-hidden="true"
                      />
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.action}
                    data-remove
                    aria-label={`Remove ${item.file.name}`}
                    onClick={() => remove(item, index)}
                  >
                    <X size={14} strokeWidth={1.75} aria-hidden="true" />
                  </button>
                </div>
              </motion.li>
            );
          })}
        </AnimatePresence>
      </ul>
      <span className={styles.srOnly} role="status" aria-live="polite">
        {announcement}
      </span>
    </div>
  );
}
FileUpload.displayName = "FileUpload";
export default FileUpload;
